import { atom } from 'recoil';
import { DicomProcessor } from '~/domain/services/dicomProcessor';

export const dicomProcessorState = atom<DicomProcessor>({
    key: 'dicomProcessorState',
    default: new DicomProcessor(),
});

/**
 * Monotonic version of the {@link DicomProcessor} cache, bumped whenever
 * its contents change (import committed, series or SOP object removed).
 *
 * The DicomProcessor is NOT part of the tracked domain aggregate: it holds
 * downloaded-but-not-reconstructed series, so its mutations emit no
 * changeset and `useDomainVersion()` cannot see them. Any derivation that
 * reads `dicomProcessor.patients` must depend on this as well — otherwise
 * deleting a raw series, or an import in which every series fails Image3D
 * construction, changes nothing React can observe and the list silently
 * keeps rendering stale rows.
 *
 * Prefer `useBumpDicomProcessorVersion()` over setting this directly.
 */
export const dicomProcessorVersionState = atom<number>({
    key: 'dicomProcessorVersion',
    default: 0,
});

export const filesProcessedState = atom<number>({
    key: 'filesProcessedState',
    default: 0,
});

export const filesToProcessState = atom<number | undefined>({
    key: 'filesToProcessState',
    default: undefined,
});

/**
 * Pipeline stage of the open-in-viewer flow. `confirming` waits on the
 * incomplete-retrieval decision, `loading` on the viewport; both still own
 * the processor, so neither is `idle`.
 */
export type ConnectorViewerOpenPhase =
    | 'idle'
    | 'resolving'
    | 'downloading'
    | 'confirming'
    | 'processing'
    | 'loading';

/**
 * Exclusive-feed lock over the shared DicomProcessor: while this is not
 * `'idle'`, other DICOM entry points refuse to feed it. Lives beside the
 * processor state, not in the feature, so core hooks read it without
 * depending on the feature folder. Held across the navigation into the
 * viewer: the job queue's "Load results" is mounted above the router and
 * live on the worklist too.
 */
export const connectorViewerOpenPhaseState = atom<ConnectorViewerOpenPhase>({
    key: 'connectorViewerOpenPhase',
    default: 'idle',
});
